import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';

import {
  createAIConversation,
  fetchInitialConversations,
  fetchConversationMessagesById,
  openChat,
  sendLlmMessage,
  setCurrentConversationId,
  setPlatformSpecificTools,
  setPlatformSpecificToolsHandlers,
} from 'app-components/flux/llmInterface';
import * as llmSelectors from 'app-components/flux/llmInterface/llmSelectors';
import type { ConversationItem } from 'app-components/flux/llmInterface/llmTypes';
import type RootState from 'app-components/utils/redux-store/rootState';
import { getLogger } from 'app-components/utils/core';
import { useAppDispatch, useAppSelector } from 'hooks/redux';

import { buildWebToolHandlers, buildWebTools, WEB_BUILD_CHART_FLAG_KEY } from 'components/AIChat/llmWebTools';

const log = getLogger('ChatSession');

const EMPTY_MESSAGES: ConversationItem[] = [];

type ChatSessionValue = {
  conversationId: string | null;
  messages: ConversationItem[];
  streamingMessage: string;
  reasoningMessage: string | null;
  sendMessage: (text: string) => Promise<void>;
  startNewConversation: () => Promise<void>;
  prepareConversation: () => Promise<void>;
  loadConversation: (id: string) => Promise<void>;
  isReady: boolean;
  error: string | null;
};

const ChatSessionContext = createContext<ChatSessionValue | null>(null);

type ChatSessionProviderProps = {
  children: ReactNode;
};

export const ChatSessionProvider = ({ children }: ChatSessionProviderProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const hasInitialized = useRef(false);
  const inFlightRef = useRef<Promise<void> | null>(null);

  const chatUIState = useAppSelector(llmSelectors.getChatUIState);
  const conversationId = useAppSelector(llmSelectors.getCurrentConversationId);
  const chartsEnabled = useAppSelector((state) => {
    const typedState = state as Partial<Pick<RootState, 'featureFlagsStore'>>;
    return Boolean(typedState.featureFlagsStore?.featureFlags?.get(WEB_BUILD_CHART_FLAG_KEY));
  });

  const conversationData = useAppSelector((state) =>
    conversationId ? llmSelectors.selectConversation(state, conversationId) : null,
  );

  const messages = conversationData?.messages ?? EMPTY_MESSAGES;
  const streamingMessage = conversationData?.streamingMessage ?? '';
  const reasoningMessage = conversationData?.reasoningMessage ?? null;
  const isLoading = conversationData?.isLoading ?? false;
  const error = conversationData?.error ?? null;

  useEffect(() => {
    dispatch(setPlatformSpecificTools(buildWebTools({ enableCharts: chartsEnabled })));
    dispatch(setPlatformSpecificToolsHandlers(buildWebToolHandlers({ enableCharts: chartsEnabled })));
  }, [dispatch, chartsEnabled]);

  useEffect(() => {
    if (hasInitialized.current) return;

    if (chatUIState === 'minimized' && conversationId) {
      log.debug('ChatSession: Resuming minimized conversation', { conversationId });
      hasInitialized.current = true;
      dispatch(openChat());
      dispatch(fetchConversationMessagesById(conversationId));
    }
  }, [dispatch, chatUIState, conversationId]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!conversationId) return;
      await dispatch(sendLlmMessage({ conversationId, text })).unwrap();
    },
    [dispatch, conversationId],
  );

  const prepareConversation = useCallback(async (): Promise<void> => {
    if (conversationId) return;
    if (inFlightRef.current) {
      await inFlightRef.current;
      return;
    }

    const promise = (async () => {
      try {
        const conversationsRes = await dispatch(fetchInitialConversations()).unwrap();
        const existingConversations = conversationsRes.content;

        if (existingConversations?.length) {
          const latestId = existingConversations[0].id;
          await dispatch(fetchConversationMessagesById(latestId)).unwrap();
          dispatch(setCurrentConversationId(latestId));
        } else {
          const result = await dispatch(createAIConversation()).unwrap();
          dispatch(setCurrentConversationId(result.id));
        }
        hasInitialized.current = true;
      } catch (err) {
        log.error('Failed to prepare conversation', err);
      }
    })();

    inFlightRef.current = promise;
    try {
      await promise;
    } finally {
      inFlightRef.current = null;
    }
  }, [dispatch, conversationId]);

  const startNewConversation = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) {
      await inFlightRef.current;
      return;
    }

    const promise = (async () => {
      try {
        const result = await dispatch(createAIConversation()).unwrap();
        dispatch(setCurrentConversationId(result.id));
      } catch (err) {
        log.error('Failed to start a new conversation', err);
      }
    })();

    inFlightRef.current = promise;
    try {
      await promise;
    } finally {
      inFlightRef.current = null;
    }
  }, [dispatch]);

  const loadConversation = useCallback(
    async (convId: string): Promise<void> => {
      try {
        await dispatch(fetchConversationMessagesById(convId)).unwrap();
        dispatch(setCurrentConversationId(convId));
      } catch (err) {
        log.error('Failed to load conversation', err);
      }
    },
    [dispatch],
  );

  const value = useMemo(
    () => ({
      conversationId,
      messages,
      streamingMessage,
      reasoningMessage,
      sendMessage,
      startNewConversation,
      prepareConversation,
      loadConversation,
      isReady: !!conversationId && !isLoading,
      error,
    }),
    [
      conversationId,
      messages,
      streamingMessage,
      reasoningMessage,
      sendMessage,
      startNewConversation,
      prepareConversation,
      loadConversation,
      isLoading,
      error,
    ],
  );

  return <ChatSessionContext.Provider value={value}>{children}</ChatSessionContext.Provider>;
};

export const useChatSession = (): ChatSessionValue => {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSession must be used within ChatSessionProvider');
  }
  return context;
};
